import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Globe } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { COMPANY } from "@/data/site";
import { useScrollPosition } from "@/hooks/useScrollPosition";

const navKeys = [
  { key: "home" as const, href: "/" },
  { key: "about" as const, href: "/about" },
  { key: "capabilities" as const, href: "/capabilities" },
  { key: "products" as const, href: "/products" },
  { key: "cnc" as const, href: "/cnc" },
  { key: "steel" as const, href: "/steel" },
  { key: "coating" as const, href: "/coating" },
  { key: "catalogs" as const, href: "/catalogs" },
  { key: "gallery" as const, href: "/gallery" },
  { key: "contact" as const, href: "/contact" },
];

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, lang, toggle } = useLanguage();
  const scrollY = useScrollPosition();
  const location = useLocation();
  const hasShadow = scrollY > 100;

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
          hasShadow ? "shadow-nav" : "border-b border-muted-border"
        }`}
      >
        <div className="content-container">
          <nav className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 font-display font-extrabold text-xl text-primary-deep tracking-tight shrink-0"
            >
              <img
                src="/images/logo.jpg"
                alt="Sky Mark Co."
                className="h-10 w-10 rounded-lg object-cover border border-muted-border"
              />
              <span className="hidden md:inline">
                SKY <span className="text-secondary-blue">MARK</span>
                <span className="text-gold">.</span> CO
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden xl:flex items-center gap-1 ml-6">
              {navKeys.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-2.5 py-2 text-[0.85rem] font-body font-medium transition-colors duration-200 ${
                    isActive(link.href)
                      ? "text-secondary-blue"
                      : "text-steel hover:text-secondary-blue"
                  }`}
                >
                  {t(link.key)}
                  {isActive(link.href) && (
                    <span className="absolute bottom-0 left-2.5 right-2.5 h-0.5 bg-secondary-blue rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden xl:flex items-center gap-3 ml-auto">
              {/* Language Toggle */}
              <button
                onClick={toggle}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-body font-semibold text-primary-deep border border-muted-border hover:bg-steel-light transition-colors"
                aria-label={t("language")}
              >
                <Globe className="w-4 h-4" />
                {lang === "en" ? "العربية" : "English"}
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary-deep text-white text-sm font-body font-semibold transition-all duration-300 hover:bg-secondary-blue hover:shadow-card-hover"
              >
                {t("sendInquiry")}
              </Link>
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-success text-white text-sm font-body font-semibold transition-all duration-300 hover:bg-green-600 hover:shadow-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                {t("whatsapp")}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 text-primary-deep"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-primary-deep/95 backdrop-blur-sm transition-opacity duration-300 xl:hidden ${
          isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col h-full pt-20 pb-8 px-6 overflow-y-auto">
          {/* Language Toggle Mobile */}
          <button
            onClick={() => {
              toggle();
            }}
            className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-lg bg-white/10 text-white text-sm font-semibold"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "العربية" : "English"}
          </button>

          <div className="flex flex-col gap-1">
            {navKeys.map((link, index) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`py-3 text-lg font-body font-medium transition-colors duration-200 border-b border-white/10 ${
                  isActive(link.href)
                    ? "text-gold-base"
                    : "text-white/90 hover:text-gold-base"
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <div className="mt-auto pt-6 border-t border-white/20 space-y-3">
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="block w-full text-center py-3 rounded-md bg-white text-primary-deep font-body font-semibold text-base"
            >
              {t("sendInquiry")}
            </Link>
            <a
              href={COMPANY.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-md bg-success text-white font-body font-semibold text-base"
            >
              <MessageCircle className="w-5 h-5" />
              {t("whatsappUs")}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
